import { createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { type AxiosResponse } from "axios";
import { awsClientGateway } from "./common/gateway";
import { logMessage } from "../utils/amplifyLogger";
import { clientLog } from "./logging";
import type { RootState } from "../store/types";
import { getInstanceId } from "./common/serviceUtils";

type RejectFn = (value: string) => unknown;

const getMediaMgtData = async (
  instanceId: string,
  rejectWithValue: RejectFn,
): Promise<AxiosResponse | unknown> => {
  try {
    const response = await awsClientGateway({
      pathTemplate: `/mediaManagement/${instanceId}?apiName=mediaMgt`,
      method: "GET",
    });
    return response;
  } catch (error) {
    clientLog({
      component: `mediaMgt`,
      message: `Something went wrong in getMediaMgtData API => ${error}`,
    });
    logMessage(
      `mediaMgt`,
      `Something went wrong in getMediaMgtData API => ${error}`,
      `error`,
    );
    return rejectWithValue("Error in getMediaMgtData api endpoint");
  }
};

export const getMediaMgt = createAsyncThunk<
  unknown,
  void,
  { state: RootState }
>("mediaMgt/getMediaMgt", async (_args, { getState, rejectWithValue }) => {
  const instanceId = getInstanceId(getState());
  return await getMediaMgtData(instanceId, rejectWithValue);
});

export interface MediaFileArg {
  instanceId: string;
  languageCode: string;
  language: string;
  fileName: string;
  type: string;
  [key: string]: unknown;
}

export const getMediaFile = createAsyncThunk<
  AxiosResponse | unknown,
  MediaFileArg
>(
  "mediaMgt/getMediaFile",
  async (mediaMgtData: MediaFileArg, { rejectWithValue }) => {
    const { instanceId, languageCode, language, fileName, type } = mediaMgtData;
    try {
      const response = await awsClientGateway({
        pathTemplate: `/mediaFile/${instanceId}/${languageCode}-${language}/${fileName}/${type}`,
        method: "GET",
      });
      return response;
    } catch (error) {
      clientLog({
        component: `mediaMgt`,
        message: `Something went wrong in getMediaFile API => ${error}`,
      });
      logMessage(
        `mediaMgt`,
        `Something went wrong in getMediaFile API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in getMediaFile api endpoint");
    }
  },
);

export const createOrUpdateMediaMgt = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "mediaMgt/createOrUpdateMediaMgt",
  async (mediaMgtData, { getState, rejectWithValue }) => {
    try {
      const instanceId = getInstanceId(getState());
      await awsClientGateway({
        pathTemplate: `/mediaManagement/${instanceId}`,
        method: "POST",
        body: { ...mediaMgtData },
      });
      return await getMediaMgtData(instanceId, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `mediaMgt`,
        message: `Something went wrong in createOrUpdateMediaMgt API => ${error}`,
      });
      logMessage(
        `mediaMgt`,
        `Something went wrong in createOrUpdateMediaMgt API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in createOrUpdateMediaMgt api endpoint");
    }
  },
);

export const deleteMediaMgt = createAsyncThunk<
  unknown,
  Record<string, unknown>,
  { state: RootState }
>(
  "mediaMgt/deleteMediaMgt",
  async (mediaMgtData, { getState, rejectWithValue }) => {
    try {
      const instanceId = getInstanceId(getState());
      await awsClientGateway({
        pathTemplate: `/mediaManagement/${instanceId}`,
        method: "DELETE",
        body: { ...mediaMgtData },
      });
      return await getMediaMgtData(instanceId, rejectWithValue);
    } catch (error) {
      clientLog({
        component: `mediaMgt`,
        message: `Something went wrong in deleteMediaMgt API => ${error}`,
      });
      logMessage(
        `mediaMgt`,
        `Something went wrong in deleteMediaMgt API => ${error}`,
        `error`,
      );
      return rejectWithValue("Error in deleteMediaMgt api endpoint");
    }
  },
);

export const getSubActiveTabId = createAction("mediaMgt/getSubActiveTabId");

export const getMediaExpandCollapseInfo = createAction<{
  mediaId: string | undefined;
  isExpanded: boolean;
}>("mediaMgt/getMediaExpandCollapseInfo");

export const getResetMediaMgt = createAction("mediaMgt/getResetMediaMgt");
export const getMediaShowEdit = createAction<Record<string, unknown>>(
  "mediaMgt/getMediaShowEdit",
);
export const getMediaHideEdit = createAction("mediaMgt/getMediaHideEdit");
